/**
 * AuthProvider - Handles JWT authentication when wallet connects.
 * 
 * This provider wraps the app and:
 * 1. Detects wallet connection
 * 2. Silently restores valid sessions (no user prompt)
 * 3. On explicit user action, signs challenge with wallet
 * 4. Stores JWT token and passes to WebSocket client
 * 
 * IMPORTANT: We do NOT auto-prompt for wallet signing on page load.
 * autoConnect restores the wallet connection silently, but the signing
 * prompt only appears when the user explicitly clicks "Connect wallet / Login".
 * This follows industry standards (Jupiter, Drift, Raydium, etc.).
 */

import { createContext, useCallback, useEffect, useRef, type ReactNode } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { toast } from "sonner";
import { useAuthToken } from "./hooks/use-auth-token";
import { getStoredToken, getStoredWallet } from "./auth";

export interface AuthContextValue {
  /** JWT token if authenticated */
  token: string | null;
  /** Whether authentication is in progress */
  isAuthenticating: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** User ID (database UUID from backend) */
  userId: string | null;
  /** Wallet address for display and signing */
  walletAddress: string | null;
  /** User's linked email (for wallet users who linked email) */
  email: string | null;
  /** Login method: 'wallet', 'privy', or 'both' */
  loginMethod: string | null;
  /** Whether this is a brand new user (first signup) - for Meta Pixel Lead tracking */
  isNewUser: boolean;
  /** Manually trigger authentication */
  authenticate: () => Promise<void>;
  /** Clear authentication */
  logout: () => void;
  /** Reset auth attempt tracking to allow re-prompting sign message */
  resetAuthAttempt: () => void;
  /** Signal that the user explicitly wants to authenticate (clicked login button) */
  markUserInitiated: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { connected, publicKey, signMessage } = useWallet();
  const {
    token,
    isAuthenticating,
    isAuthenticated,
    userId,
    walletAddress,
    email,
    loginMethod,
    isNewUser,
    authenticate,
    logout,
    error,
  } = useAuthToken();
  
  // Track if we've already attempted auth for this wallet connection
  const authAttemptedRef = useRef<string | null>(null);
  const toastIdRef = useRef<string | number | null>(null);

  // Function to reset auth attempt - allows re-triggering sign message when user clicks connect again
  const resetAuthAttempt = useCallback(() => {
    authAttemptedRef.current = null;
  }, []);

  // Track whether user has explicitly requested authentication (clicked login)
  const userInitiatedRef = useRef(false);

  // Allow external callers (e.g. LoginModal) to mark auth as user-initiated
  const markUserInitiated = useCallback(() => {
    userInitiatedRef.current = true;
  }, []);

  // Authenticate when wallet connects — but ONLY if appropriate:
  // 1. Silent re-auth: A valid stored token already exists for this wallet
  //    (user was previously logged in, token hasn't expired → no popup needed)
  // 2. User-initiated: User explicitly clicked "Connect wallet / Login"
  //    (they expect the signing prompt → show toast + Phantom popup)
  //
  // We NEVER auto-prompt for signing on page load. autoConnect silently
  // restores the wallet connection, but signing only happens on user action.
  useEffect(() => {
    const walletAddress = publicKey?.toBase58() || null;
    
    if (
      !connected ||
      !walletAddress ||
      isAuthenticated ||
      authAttemptedRef.current === walletAddress ||
      !signMessage ||
      isAuthenticating
    ) {
      return;
    }

    // Check if there's an existing stored session for this wallet
    const storedToken = getStoredToken();
    const storedWallet = getStoredWallet();
    const hasStoredSession = !!storedToken && storedWallet === walletAddress;

    // If there's a stored session, use-auth-token.ts already restored it on mount.
    // The isAuthenticated check above covers that case, so if we're here it means
    // the stored session was invalid/expired. Only proceed if user initiated.
    if (!hasStoredSession && !userInitiatedRef.current) {
      // Wallet auto-connected but no valid session and user didn't click login.
      // Don't prompt — let the user browse freely.
      return;
    }

    // Mark that we're attempting auth for this wallet
    authAttemptedRef.current = walletAddress;
    // Reset user-initiated flag after consuming it
    userInitiatedRef.current = false;

    // Start authentication
    const doAuth = async () => {
      // Show loading toast
      toastIdRef.current = toast.loading("Authenticating...", {
        description: "Please sign the message in your wallet",
      });

      try {
        await authenticate();
        
        toast.success("Securely Connected", {
          id: toastIdRef.current,
          description: "Your account is now protected with real-time sync.",
          duration: 4000,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Authentication failed";
        const isUserRejection = message.includes("User rejected") || message.includes("rejected");
        
        if (isUserRejection) {
          toast.info("Authentication skipped", {
            id: toastIdRef.current,
            description: "You can authenticate later for real-time updates",
            duration: 4000,
          });
        } else {
          toast.error("Authentication failed", {
            id: toastIdRef.current,
            description: message,
            duration: 5000,
          });
          // Reset attempt tracking only for actual errors so user can retry
          authAttemptedRef.current = null;
        }
      }
    };

    // Small delay to let wallet UI settle
    const timeout = setTimeout(doAuth, 500);
    return () => clearTimeout(timeout);
  }, [connected, publicKey, isAuthenticated, signMessage, isAuthenticating, authenticate, token]);

  // Reset auth attempt tracking when wallet disconnects
  useEffect(() => {
    if (!connected) {
      authAttemptedRef.current = null;
    }
  }, [connected]);

  // Log auth errors
  useEffect(() => {
    if (error) {
      console.error("[AuthProvider] Auth error:", error);
    }
  }, [error]);

  const value: AuthContextValue = {
    token,
    isAuthenticating,
    isAuthenticated,
    userId,
    walletAddress,
    email,
    loginMethod,
    isNewUser,
    authenticate,
    logout,
    resetAuthAttempt,
    markUserInitiated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
